import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { StatusPill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { FileText, Users, Receipt, AlertTriangle, ArrowRight, Plus } from "lucide-react";

export default function FacilityDashboard() {
  const [data, setData] = useState({ requests: [], assignments: [], invoices: [], incidents: [] });

  useEffect(() => {
    Promise.all([
      api.get("/requests"),
      api.get("/assignments"),
      api.get("/invoices"),
      api.get("/incidents"),
    ]).then(([r, a, i, inc]) => {
      setData({ requests: r.data, assignments: a.data, invoices: i.data, incidents: inc.data });
    });
  }, []);

  const stats = [
    { label: "Open Requests", value: data.requests.filter((r) => !["completed", "cancelled"].includes(r.status)).length, icon: FileText, color: "#0B67C2" },
    { label: "Active Assignments", value: data.assignments.filter((a) => ["confirmed", "checked_in"].includes(a.status)).length, icon: Users, color: "#16B3A8" },
    { label: "Open Invoices", value: data.invoices.filter((i) => i.status !== "paid").length, icon: Receipt, color: "#F59E0B" },
    { label: "Incidents", value: data.incidents.filter((i) => i.status !== "resolved").length, icon: AlertTriangle, color: "#DC2626" },
  ];

  return (
    <div className="space-y-8" data-testid="facility-dashboard">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-brand-navy" style={{ fontFamily: "Outfit, sans-serif" }}>Facility Overview</h1>
          <p className="text-slate-500 mt-1">Track your staffing requests and assignments at a glance.</p>
        </div>
        <Link to="/facility/requests/new">
          <Button className="bg-brand-navy hover:bg-brand-blue" data-testid="facility-new-request-btn">
            <Plus className="w-4 h-4 mr-2" />New staffing request
          </Button>
        </Link>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s) => {
          const Icon = s.icon;
          return (
            <Card key={s.label} className="border-slate-200">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{s.label}</div>
                    <div className="mt-2 text-3xl font-bold text-brand-navy" style={{ fontFamily: "Outfit, sans-serif" }}>{s.value}</div>
                  </div>
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${s.color}15` }}>
                    <Icon className="w-5 h-5" style={{ color: s.color }} />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between"><CardTitle className="text-base">Recent requests</CardTitle><Link to="/facility/requests" className="text-sm text-brand-teal hover:underline">View all <ArrowRight className="inline w-3 h-3" /></Link></CardHeader>
          <CardContent className="space-y-3">
            {data.requests.slice(0, 5).map((r) => (
              <div key={r.id} className="flex items-center justify-between p-3 rounded-lg border border-slate-200 hover:border-brand-teal transition-colors" data-testid={`facility-request-${r.id}`}>
                <div>
                  <div className="font-semibold text-slate-900">{r.position} × {r.count}</div>
                  <div className="text-xs text-slate-500">{r.shift_date} · {r.start_time}-{r.end_time}</div>
                </div>
                <StatusPill status={r.status} />
              </div>
            ))}
            {data.requests.length === 0 && <div className="text-sm text-slate-500 text-center py-6">No requests yet. <Link to="/facility/requests/new" className="text-brand-teal font-semibold">Create one</Link></div>}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between"><CardTitle className="text-base">Recent invoices</CardTitle><Link to="/facility/invoices" className="text-sm text-brand-teal hover:underline">View all <ArrowRight className="inline w-3 h-3" /></Link></CardHeader>
          <CardContent className="space-y-3">
            {data.invoices.slice(0, 5).map((inv) => (
              <div key={inv.id} className="flex items-center justify-between p-3 rounded-lg border border-slate-200" data-testid={`facility-invoice-${inv.id}`}>
                <div>
                  <div className="font-semibold text-slate-900">{inv.invoice_number}</div>
                  <div className="text-xs text-slate-500">Due {inv.due_date} · ${inv.total.toFixed(2)} CAD</div>
                </div>
                <StatusPill status={inv.status} />
              </div>
            ))}
            {data.invoices.length === 0 && <div className="text-sm text-slate-500 text-center py-6">No invoices yet.</div>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
